// src/rms/pricing/hours.js
// ============================================================================
// Opening hours
// ============================================================================
// Pure functions over the two tables in 20260821100000. No React, no adapter,
// no fetching -- the caller passes the rows in, exactly as availability.js does,
// so this is testable and so the same answer can be computed on a screen, in a
// form, and eventually in SQL without three implementations drifting.
//
// ----------------------------------------------------------------------------
// Precedence, in one sentence
// ----------------------------------------------------------------------------
// An exception for the date beats the weekday pattern; no rule at all means
// open. That last clause is the one to remember: a business that has never
// opened this screen is open around the clock, because the alternative is a
// brand new operator unable to take a booking for a reason nothing on screen
// explains.
//
// ----------------------------------------------------------------------------
// The timezone, and what this file honestly does about it
// ----------------------------------------------------------------------------
// Hours are local to rms_operators.timezone. The comparisons below read the
// wall-clock of a JS Date in the BROWSER's zone, which is correct whenever the
// person at the counter is in the same zone as the counter -- which is the
// normal case, and the only case a rental desk actually has.
//
// It is wrong for an owner reviewing bookings from another country. That is a
// real limitation and it is written down rather than hidden: fixing it properly
// needs Intl.DateTimeFormat with the operator's zone on every comparison, and
// it should be done before this is used for anything that refuses a write. It
// is used for a warning today.
// ============================================================================

/** Sunday-first, matching Date.getDay() and Postgres EXTRACT(DOW). */
export const WEEKDAYS = [
  { value: 0, key: "weekday.sunday", label: "Sunday" },
  { value: 1, key: "weekday.monday", label: "Monday" },
  { value: 2, key: "weekday.tuesday", label: "Tuesday" },
  { value: 3, key: "weekday.wednesday", label: "Wednesday" },
  { value: 4, key: "weekday.thursday", label: "Thursday" },
  { value: 5, key: "weekday.friday", label: "Friday" },
  { value: 6, key: "weekday.saturday", label: "Saturday" },
];

/** yyyy-mm-dd from a Date, in its own zone. Never toISOString, which is UTC. */
export const dateKey = (d) =>
  [d.getFullYear(), String(d.getMonth() + 1).padStart(2, "0"), String(d.getDate()).padStart(2, "0")].join("-");

/** "09:30" or "09:30:00" -> minutes since midnight. */
export function minutesOf(time) {
  if (!time) return null;
  const [h, m] = String(time).split(":");
  const hours = Number(h);
  const mins = Number(m || 0);
  return Number.isFinite(hours) && Number.isFinite(mins) ? hours * 60 + mins : null;
}

/**
 * The rule in force on one date.
 *
 * Returns { open, opensAt, closesAt, source, label } where source is
 * "exception" | "weekday" | "unset". Callers that only need a boolean can read
 * .open, but the source is what lets a screen say WHY -- "closed on Sundays"
 * and "closed for Christmas Day" are different sentences and an operator
 * checking their own configuration needs to see which one applied.
 */
export function ruleFor(date, { weekly = [], exceptions = [] } = {}) {
  const key = dateKey(date);

  const exception = exceptions.find((e) => e.on_date === key);
  if (exception) {
    return {
      open: !exception.is_closed,
      opensAt: exception.is_closed ? null : exception.opens_at,
      closesAt: exception.is_closed ? null : exception.closes_at,
      source: "exception",
      label: exception.label || null,
    };
  }

  const day = weekly.find((w) => Number(w.weekday) === date.getDay());
  if (day) {
    return {
      open: !day.is_closed,
      opensAt: day.is_closed ? null : day.opens_at,
      closesAt: day.is_closed ? null : day.closes_at,
      source: "weekday",
      label: null,
    };
  }

  // Nothing configured for this day. Open -- see the header.
  return { open: true, opensAt: null, closesAt: null, source: "unset", label: null };
}

/**
 * Is this exact moment inside the counter's opening hours?
 *
 * A day with no times on it (source "unset", or an exception that only names
 * the date) is open all day rather than closed, which keeps a half-filled
 * configuration from quietly refusing everything.
 */
export function isOpenAt(date, config) {
  const rule = ruleFor(date, config);
  if (!rule.open) return false;

  const opens = minutesOf(rule.opensAt);
  const closes = minutesOf(rule.closesAt);
  if (opens === null || closes === null) return true;

  const at = date.getHours() * 60 + date.getMinutes();
  // Half-open: a return booked exactly at closing time is inside the day, a
  // pickup booked exactly at closing time is not.
  return at >= opens && at <= closes;
}

/**
 * Why a moment falls outside opening hours, or null when it does not.
 *
 * Returns a shape rather than a sentence, so the copy stays in the catalogues
 * and this module stays free of English.
 */
export function closedReason(date, config) {
  const rule = ruleFor(date, config);
  if (!rule.open) {
    return { kind: rule.source === "exception" ? "closedDate" : "closedDay", label: rule.label };
  }
  if (isOpenAt(date, config)) return null;
  return { kind: "outsideHours", opensAt: rule.opensAt, closesAt: rule.closesAt };
}

/** Both ends of a booking, checked together. Returns [] when both are fine. */
export function bookingWarnings(startsAt, endsAt, config) {
  const out = [];
  if (startsAt) {
    const why = closedReason(new Date(startsAt), config);
    if (why) out.push({ leg: "start", ...why });
  }
  // An open-ended booking has no return leg to check.
  if (endsAt) {
    const why = closedReason(new Date(endsAt), config);
    if (why) out.push({ leg: "end", ...why });
  }
  return out;
}

export default { WEEKDAYS, dateKey, minutesOf, ruleFor, isOpenAt, closedReason, bookingWarnings };
